import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Input, Message, Radio } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { IconPlus } from '@arco-design/web-react/icon';
import { get } from 'lodash';

import { SectionHeader } from '@/components/SectionHeader';
import { MAAS_INPUT_PLACEHOLDER, maasRequiredRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import { DEFAULT_MAAS_OPTIONS, type MaasOptions } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { getModelSizeGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
import type {
  MaasModelSourceEntry,
  ModelInstanceFormValues,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { parseStorageQuantityToGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/quantity';
import {
  MODEL_DATA_VOLUME_NAME,
  getVolumeClaimDisplayName,
  getVolumeClaimName,
  getVolumePvcTemplate,
  getVolumeStorageClassInfo,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import { MaasDataDiskMode, getDataDiskMode } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';
import {
  MODEL_DATA_DISK_ACCESS_MODES,
  type MaasDataDiskCardItem,
  MaasDataDiskCreateModal,
  MaasDataDiskItems,
  MaasDataDiskSelectModal,
} from '@/delivery-type-plugin/components/maas-data-disk';
import { usePersistentVolumeClaims } from '@/delivery-type-plugin/hooks/usePersistentVolumeClaims';
import { useOptionalResourceFormContext } from '@/delivery-type-plugin/platform/resource-form';

import { ExistingModelStorageCard } from './existing-model-storage-card';
import { hasPresetModelStorage } from './hasPresetModelStorage';
import styles from './maas-storage-setting.module.less';
import { ModelConfigureModal } from './model-configure-modal';
import { ModelFieldsEditModal } from './model-fields-edit-modal';
import { ModelSourcesGrid } from './model-source-entry-card';
import { validateStorageModalModelFields } from './model-source-form-rules';
import { MAX_MODEL_SOURCES, buildExistingPvcModelSourceEntry, resolveLockedModelSourceType } from './modelSources';
import { useMaasStorageState } from './useMaasStorageState';
import {
  clearModelSourceDraftFields,
  readModelSourcesFromForm,
  resetModelSourceDraftForAdd,
  useModelSourcesList,
} from './useModelSourcesList';

const FormItem = Form.Item;

type StorageMode = 'new' | 'existing';

interface MaasStorageSettingMultiProps {
  form: FormInstance;
  skuOuterForm?: FormInstance;
  typeOptions?: MaasOptions;
  initValues?: ModelInstanceFormValues;
  createStorageOnly?: boolean;
  isEdit?: boolean;
  billModelId?: string;
  deploymentTypeId?: string;
  paymentTiming?: string;
  skuType?: string;
  resourceEditMode?: string;
  lockActionsWhenFilled?: boolean;
  resourceCode?: string;
  onPriceChange?: (outerValues: Record<string, unknown>, innerForm: FormInstance) => void | Promise<void>;
}

/** 模型实例多源：Radio 分流 + 独立模型/存储弹窗 */
export function MaasStorageSettingMulti({
  form,
  skuOuterForm,
  typeOptions = DEFAULT_MAAS_OPTIONS,
  initValues,
  createStorageOnly,
  isEdit,
  billModelId: billModelIdProp,
  deploymentTypeId: deploymentTypeIdProp,
  paymentTiming,
  skuType,
  resourceEditMode,
  lockActionsWhenFilled: lockActionsWhenFilledProp,
  resourceCode = 'DataVolume',
  onPriceChange,
}: MaasStorageSettingMultiProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const resourceFormCtx = useOptionalResourceFormContext();
  const billModelId = billModelIdProp ?? resourceFormCtx?.billModelId;
  const deploymentTypeId = deploymentTypeIdProp ?? resourceFormCtx?.deploymentTypeId;
  const resolvedPaymentTiming = paymentTiming ?? resourceFormCtx?.paymentTiming;
  const resolvedSkuType = skuType ?? resourceFormCtx?.skuType;
  const resolvedResourceEditMode = resourceEditMode ?? resourceFormCtx?.resourceEditMode;
  const isOrderFixed = resolvedResourceEditMode === 'order-fixed';
  const lockActionsWhenFilled = lockActionsWhenFilledProp === true;
  const locked = sectionReadOnly || isOrderFixed;

  const storageMode = (Form.useWatch('_modelStorageMode', form) as StorageMode | undefined) ?? 'new';
  const sources = (Form.useWatch('model.sources', form) as MaasModelSourceEntry[] | undefined) ?? [];
  const watchedModel = Form.useWatch('model', form);
  const modelMountPath = Form.useWatch('model.mountPath', form) as string | undefined;

  const { appendFromDraft, updateEntryAt, removeAt, clearAllSources, replaceAll } = useModelSourcesList(form);

  const [addModelOpen, setAddModelOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number>();
  const [storageOnlyModalOpen, setStorageOnlyModalOpen] = useState(false);

  const { options: pvcOptions } = usePersistentVolumeClaims({ resources_filter: undefined, status: 'available' });
  const pvcDisplayNameByClaim = useMemo(
    () => Object.fromEntries(pvcOptions.map((item) => [item.value, item.label || item.value])),
    [pvcOptions],
  );

  const volumeToCardItem = useCallback(
    (volume: Record<string, unknown>): MaasDataDiskCardItem => {
      const mode = getDataDiskMode(volume) || MaasDataDiskMode.Existing;
      if (mode === MaasDataDiskMode.Create) {
        const template = getVolumePvcTemplate(volume);
        const storageRaw = get(template, 'resources.requests.storage');
        const storageGi = parseStorageQuantityToGi(storageRaw);
        return {
          mode,
          name: String(volume.name || t('components:create.data.disk')),
          storageClassInfo: getVolumeStorageClassInfo(volume),
          storageGi: storageGi ?? (storageRaw !== undefined && storageRaw !== null ? String(storageRaw) : undefined),
          accessModes: get(template, 'accessModes') as string[] | undefined,
          reclaimPolicy: volume.reclaimPolicy ? String(volume.reclaimPolicy) : undefined,
        };
      }
      const claimName = getVolumeClaimName(volume);
      const displayName =
        getVolumeClaimDisplayName(volume) || (claimName ? pvcDisplayNameByClaim[claimName] : undefined) || claimName || '';
      return { mode, name: displayName, reclaimPolicy: volume.reclaimPolicy ? String(volume.reclaimPolicy) : undefined, claimName };
    },
    [pvcDisplayNameByClaim, t],
  );

  const {
    selectModalOpen,
    editingVolume,
    dataVolume,
    hasDataDisk,
    handleSaveVolume,
    handleSaveExistingVolume,
    clearStorageVolume,
    openSelectModal,
    openEditModal,
    closeSelectModal,
  } = useMaasStorageState({
    form,
    initValues,
    modelMountPath,
    watchedModel,
    createStorageOnly,
    modelInstanceMultiSource: true,
  });

  const cardItem = useMemo(
    () => (hasDataDisk && dataVolume ? volumeToCardItem(dataVolume) : undefined),
    [hasDataDisk, dataVolume, volumeToCardItem],
  );

  const presetHasDataDisk = useMemo(() => hasPresetModelStorage(initValues as Record<string, unknown> | undefined), [initValues]);
  const actionsLockedByFill = lockActionsWhenFilled && presetHasDataDisk;
  const canMutate = !locked && !actionsLockedByFill && !isEdit;

  const modelSizeBytes = useMemo(() => {
    const gi = getModelSizeGi(watchedModel as ModelInstanceFormValues['model']);
    return gi ? Math.ceil(gi * 1024 ** 3) : undefined;
  }, [watchedModel]);

  const lockedSourceType = useMemo(() => resolveLockedModelSourceType(sources), [sources]);

  useEffect(() => {
    if (form.getFieldValue('_modelStorageMode') === undefined) {
      form.setFieldValue('_modelStorageMode', createStorageOnly ? 'new' : 'new');
    }
  }, [createStorageOnly, form]);

  const clearStorageAndModels = useCallback(() => {
    clearStorageVolume();
    clearAllSources();
  }, [clearAllSources, clearStorageVolume]);

  const handleModeChange = useCallback(
    (mode: StorageMode) => {
      if (mode === storageMode) return;
      clearStorageAndModels();
      form.setFieldValue('_modelStorageMode', mode);
    },
    [clearStorageAndModels, form, storageMode],
  );

  const handleAddModelConfirm = useCallback(async () => {
    if (!appendFromDraft()) {
      Message.error(t('data-center:maas.model.sources.same.type.required'));
      return;
    }
    setAddModelOpen(false);
    clearModelSourceDraftFields(form);
    void form.validate(['maasStorageConfigured']);
  }, [appendFromDraft, form, t]);

  const openAddModelModal = useCallback(() => {
    resetModelSourceDraftForAdd(form, lockedSourceType);
    setAddModelOpen(true);
  }, [form, lockedSourceType]);

  const closeAddModelModal = useCallback(() => {
    setAddModelOpen(false);
    clearModelSourceDraftFields(form);
  }, [form]);

  const handleExistingSave = useCallback(
    (volume: Record<string, unknown>) => {
      const name = String(form.getFieldValue('model.name') || '').trim();
      const size = String(form.getFieldValue('model.size') || '').trim();
      const entry = buildExistingPvcModelSourceEntry(name, size);
      if (entry) {
        replaceAll([entry]);
      }
      handleSaveExistingVolume(volume);
      clearModelSourceDraftFields(form);
      closeSelectModal();
      void form.validate(['maasStorageConfigured']);
    },
    [closeSelectModal, form, handleSaveExistingVolume, replaceAll],
  );

  const handleDeleteExistingCombined = useCallback(() => {
    clearStorageAndModels();
  }, [clearStorageAndModels]);

  const handleDeleteStorageOnly = useCallback(() => {
    clearStorageVolume();
  }, [clearStorageVolume]);

  const openStorageEditModal = useCallback(() => {
    setStorageOnlyModalOpen(true);
  }, []);

  /** 已有模型：编辑前从 sources[0] 回填弹窗内的服务名与模型大小 */
  const openExistingCombinedEdit = useCallback(() => {
    const entry = sources[0];
    if (entry) {
      form.setFieldsValue({
        'model.name': entry.name,
        'model.size': entry.size,
      });
    }
    openEditModal();
  }, [form, openEditModal, sources]);

  const existingSelectAfterContent = (
    <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
      <FormItem label={t('data-center:maas.service.name')} field="model.name" rules={maasRequiredRules(t('data-center:maas.service.name'))}>
        <Input placeholder={MAAS_INPUT_PLACEHOLDER} />
      </FormItem>
      <FormItem label={t('data-center:maas.model.size')} field="model.size" rules={maasRequiredRules(t('data-center:maas.model.size'))}>
        <Input placeholder="10Gi" />
      </FormItem>
    </Form>
  );

  const validateExistingModal = useCallback(() => validateStorageModalModelFields(form, false), [form]);

  const existingEntry = sources[0];
  const existingSizeGi = existingEntry ? parseStorageQuantityToGi(existingEntry.size) : undefined;

  const showExistingCard = storageMode === 'existing' && hasDataDisk && existingEntry;

  return (
    <div className={styles.root}>
      {!createStorageOnly ? (
        <Radio.Group
          type="button"
          value={storageMode}
          disabled={locked || isEdit || actionsLockedByFill}
          onChange={handleModeChange}
          style={{ marginBottom: 12 }}
        >
          <Radio value="new">{t('data-center:maas.model.storage.add.new')}</Radio>
          <Radio value="existing">{t('data-center:maas.model.storage.add.existing')}</Radio>
        </Radio.Group>
      ) : null}

      {storageMode === 'new' ? (
        <>
          <ModelSourcesGrid
            sources={sources}
            readOnly={!canMutate}
            onEditIndex={canMutate ? (index) => setEditIndex(index) : undefined}
            onDeleteIndex={canMutate ? (index) => removeAt(index) : undefined}
          />
          {canMutate && sources.length < MAX_MODEL_SOURCES ? (
            <div className={styles.addActionRow}>
              <SectionHeader inline title={t('data-center:maas.add.model')} />
              <Button icon={<IconPlus />} type="primary" onClick={openAddModelModal} />
            </div>
          ) : null}
          <MaasDataDiskItems
            item={cardItem}
            onEdit={canMutate && hasDataDisk ? openStorageEditModal : undefined}
            onDelete={canMutate && hasDataDisk ? handleDeleteStorageOnly : undefined}
            isEdit={isEdit || locked || actionsLockedByFill}
          />
          {canMutate && !hasDataDisk ? (
            <div className={styles.addActionRow}>
              <SectionHeader inline title={t('data-center:maas.storage.configure')} />
              <Button icon={<IconPlus />} type="primary" onClick={() => setStorageOnlyModalOpen(true)} />
            </div>
          ) : null}
        </>
      ) : (
        <>
          {showExistingCard ? (
            <ExistingModelStorageCard
              modelLines={
                <>
                  <div>{existingEntry.name}</div>
                  {existingSizeGi !== undefined ? <div>{`${existingSizeGi}Gi`}</div> : null}
                </>
              }
              storageLines={
                <>
                  <div>{cardItem?.name}</div>
                  {cardItem?.storageGi ? <div>{`${cardItem.storageGi} Gi`}</div> : null}
                  {cardItem?.reclaimPolicy ? <div>{cardItem.reclaimPolicy}</div> : null}
                </>
              }
              onEdit={canMutate ? openExistingCombinedEdit : undefined}
              onDelete={canMutate ? handleDeleteExistingCombined : undefined}
            />
          ) : canMutate ? (
            <div className={styles.addActionRow}>
              <SectionHeader inline title={t('data-center:maas.model.storage.configure')} />
              <Button icon={<IconPlus />} type="primary" onClick={openSelectModal} />
            </div>
          ) : null}
        </>
      )}

      <ModelConfigureModal
        open={addModelOpen}
        form={form}
        typeOptions={typeOptions}
        lockedSourceType={lockedSourceType}
        onClose={closeAddModelModal}
        onConfirm={handleAddModelConfirm}
      />
      <ModelFieldsEditModal
        open={editIndex !== undefined}
        entry={editIndex !== undefined ? sources[editIndex] : undefined}
        onClose={() => setEditIndex(undefined)}
        onSave={(patch) => {
          if (editIndex === undefined) return;
          updateEntryAt(editIndex, patch);
          void form.validate(['maasStorageConfigured']);
        }}
      />
      <MaasDataDiskCreateModal
        open={storageOnlyModalOpen}
        title={t('data-center:maas.storage.configure')}
        initVolume={
          dataVolume && getDataDiskMode(dataVolume) === MaasDataDiskMode.Create
            ? dataVolume
            : editingVolume && getDataDiskMode(editingVolume) === MaasDataDiskMode.Create
              ? editingVolume
              : undefined
        }
        modelSizeBytes={modelSizeBytes}
        defaultMountPath={modelMountPath || '/models'}
        volumeName={MODEL_DATA_VOLUME_NAME}
        hideMountPath
        fixedAccessModes={MODEL_DATA_DISK_ACCESS_MODES}
        billModelId={billModelId}
        deploymentTypeId={deploymentTypeId}
        resourceCode={resourceCode}
        paymentTiming={resolvedPaymentTiming}
        skuType={resolvedSkuType}
        resourceEditMode={resolvedResourceEditMode}
        skuOuterForm={skuOuterForm}
        resourceInnerForm={form}
        onPriceChange={onPriceChange}
        onClose={() => setStorageOnlyModalOpen(false)}
        onSave={(volume) => {
          handleSaveVolume(volume);
          setStorageOnlyModalOpen(false);
        }}
      />
      {!createStorageOnly ? (
        <MaasDataDiskSelectModal
          open={selectModalOpen}
          title={t('data-center:maas.model.storage.configure')}
          filterByModelSize={false}
          initVolume={
            dataVolume && getDataDiskMode(dataVolume) === MaasDataDiskMode.Existing
              ? dataVolume
              : editingVolume && getDataDiskMode(editingVolume) === MaasDataDiskMode.Existing
                ? editingVolume
                : undefined
          }
          defaultMountPath={modelMountPath || '/models'}
          volumeName={MODEL_DATA_VOLUME_NAME}
          hideMountPath
          afterContent={selectModalOpen ? existingSelectAfterContent : null}
          onValidateExtra={validateExistingModal}
          resourceEditMode={resolvedResourceEditMode}
          onClose={closeSelectModal}
          onSave={handleExistingSave}
        />
      ) : null}
    </div>
  );
}
